import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, X, User, BarChart3, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { ExternalLink } from "lucide-react";
import { openTopLevel } from "../lib/openTopLevel";
import { API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";
import { LeadSourcesPanel } from "../components/LeadSourcesPanel";
import { ClearDemoDataPanel } from "../components/ClearDemoDataPanel";

export default function Settings() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const readOnly = user?.role === "viewer";
  const [settings, setSettings] = useState(null);
  const [gcal, setGcal] = useState({ configured: false, redirect_uri: "" });
  const [gcalStatus, setGcalStatus] = useState(null);
  const [clients, setClients] = useState([]);
  const [creatingClient, setCreatingClient] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [profileClient, setProfileClient] = useState(null);

  const load = async () => {
    const [s, g, c, me] = await Promise.all([
      api.get("/settings"),
      api.get("/google/calendar/status"),
      api.get("/clients"),
      api.get("/auth/me"),
    ]);
    setSettings(s.data);
    setGcal(g.data);
    setClients(c.data);
    setGcalStatus(me.data.google_calendar_connected);
  };
  useEffect(() => { load(); }, []);

  const connectGcal = async () => {
    try {
      const { data } = await api.get("/google/calendar/connect");
      openTopLevel(data.authorization_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Set up Google Calendar credentials first");
    }
  };
  const disconnectGcal = async () => {
    await api.post("/google/calendar/disconnect");
    setGcalStatus(false);
    toast.success("Disconnected");
  };

  const saveSettings = async (payload) => {
    await api.put("/settings", payload);
    setSettings({ ...settings, ...payload });
    toast.success("Saved");
  };

  const [deletingClient, setDeletingClient] = useState(null); // client object pending confirm
  const removeClient = async (client) => {
    try {
      await api.delete(`/clients/${client.user_id}`);
      toast.success(`Deleted ${client.name || client.email}`);
      setDeletingClient(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete client");
    }
  };

  return (
    <div data-testid="settings-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">Configure</div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-zinc-900">Settings</h1>
      </div>

      {/* Clients */}
      <div className="mt-8 rounded-xl border border-zinc-200 bg-white p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900">Clients</h2>
            <p className="mt-1 text-sm text-zinc-600">Create accounts so clients can submit work tickets.</p>
          </div>
          {!readOnly && (
            <button data-testid="settings-add-client" onClick={() => setCreatingClient(true)} className="inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-3 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
              <Plus size={14} /> Add Client
            </button>
          )}
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {clients.map((c) => (
            <div key={c.user_id} data-testid={`settings-client-${c.user_id}`} className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-white p-4">
              {c.logo_file_id ? (
                <img src={`${API_BASE}/files/${c.logo_file_id}`} alt="" className="h-12 w-12 rounded-lg object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[#E5F0FF] text-base font-semibold text-[#0066ff]">
                  {(c.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold text-zinc-900">{c.name}</div>
                <div className="truncate text-xs text-zinc-500">{c.email}</div>
                <div className="mt-1 flex items-center gap-1 text-[10px] text-zinc-500">
                  <span className="rounded-full bg-[#E5F0FF] px-1.5 py-0.5 font-semibold text-[#0066ff]">{c.tickets_active}</span>
                  <span>active /</span>
                  <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 font-semibold">{c.tickets_complete}</span>
                  <span>complete</span>
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <button data-testid={`settings-client-${c.user_id}-profile`} onClick={() => setProfileClient(c)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100" title="Profile"><User size={14} /></button>
                {!readOnly && (
                  <button data-testid={`settings-client-${c.user_id}-edit`} onClick={() => setEditingClient(c)} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100" title="Edit"><Pencil size={14} /></button>
                )}
                {!readOnly && (
                  <button data-testid={`settings-client-${c.user_id}-delete`} onClick={() => setDeletingClient(c)} className="rounded p-1.5 text-red-500 hover:bg-red-50" title="Delete client"><Trash2 size={14} /></button>
                )}
              </div>
            </div>
          ))}
          {clients.length === 0 && (
            <div className="col-span-full rounded-md border border-dashed border-zinc-200 p-8 text-center text-sm text-zinc-500">
              No clients yet. Click "Add Client" to create the first account.
            </div>
          )}
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Google Calendar */}
        <div data-testid="settings-gcal-card" className="dt-card p-6">
          <h2 className="text-lg font-semibold tracking-tight text-zinc-900">Google Calendar</h2>
          <p className="mt-1 text-sm text-zinc-600">Connect your Google account to sync events both ways.</p>
          {!gcal.configured ? (
            <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              <div className="font-semibold">Setup required</div>
              <p className="mt-1 leading-relaxed">An admin must add Google OAuth credentials to enable Calendar sync.</p>
            </div>
          ) : gcalStatus ? (
            <div className="mt-4 flex items-center justify-between rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex items-center gap-2 text-sm text-emerald-800">
                <CheckCircle2 size={16} /> Connected
              </div>
              {!readOnly && (
                <button data-testid="settings-gcal-disconnect" onClick={disconnectGcal} className="text-sm font-medium text-emerald-700 hover:underline">Disconnect</button>
              )}
            </div>
          ) : !readOnly ? (
            <button data-testid="settings-gcal-connect" onClick={connectGcal} className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] active:scale-[0.98]">
              Connect Google Calendar
            </button>
          ) : (
            <div className="mt-4 rounded-lg border border-zinc-200 bg-zinc-50 p-4 text-sm text-zinc-600">Not connected.</div>
          )}
        </div>

        {/* Preferences */}
        {settings && (
          <div className="dt-card p-6">
            <h2 className="text-lg font-semibold tracking-tight text-zinc-900">Preferences</h2>
            <div className="mt-4 flex flex-col gap-3">
              <label className="flex items-center justify-between rounded-md border border-zinc-200 px-4 py-3 text-sm">
                <div>
                  <div className="font-medium text-zinc-900">Email notifications</div>
                  <div className="text-xs text-zinc-500">Receive a daily summary by email</div>
                </div>
                <input data-testid="settings-notif-email" type="checkbox" checked={settings.notifications?.email} onChange={(e) => saveSettings({ notifications: { ...settings.notifications, email: e.target.checked } })} className="h-4 w-4 rounded border-zinc-300 text-[#0066ff] focus:ring-[#0066ff]" />
              </label>
              <label className="flex items-center justify-between rounded-md border border-zinc-200 px-4 py-3 text-sm">
                <div>
                  <div className="font-medium text-zinc-900">Theme</div>
                  <div className="text-xs text-zinc-500">Currently only Light is available</div>
                </div>
                <select data-testid="settings-theme" value={settings.theme} onChange={(e) => saveSettings({ theme: e.target.value })} className="rounded-md border border-zinc-200 px-3 py-1.5 text-sm">
                  <option value="light">Light</option>
                </select>
              </label>
            </div>
          </div>
        )}
      </div>

      {creatingClient && <ClientModal mode="create" onClose={() => setCreatingClient(false)} onSaved={() => { setCreatingClient(false); load(); }} />}
      {editingClient && <ClientModal mode="edit" initial={editingClient} onClose={() => setEditingClient(null)} onSaved={() => { setEditingClient(null); load(); }} />}
      {profileClient && <ClientProfileModal client={profileClient} onClose={() => setProfileClient(null)} onGo={() => { navigate(`/work-orders/${profileClient.user_id}`); }} />}
      {deletingClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/25 p-4 backdrop-blur-sm" onClick={() => setDeletingClient(null)}>
          <div data-testid="delete-client-confirm" className="w-full max-w-sm rounded-2xl border border-zinc-200 bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="text-xs font-semibold uppercase tracking-widest text-red-600">Delete client</div>
            <h3 className="mt-2 text-lg font-semibold tracking-tight text-zinc-900">Remove {deletingClient.name || deletingClient.email}?</h3>
            <p className="mt-2 text-sm text-zinc-600">
              Their login and profile will be permanently deleted. Existing tickets, projects, and inspiration boards stay in place.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setDeletingClient(null)} className="rounded-md px-3 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
              <button data-testid="delete-client-confirm-btn" onClick={() => removeClient(deletingClient)} className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white hover:bg-red-700">
                <Trash2 size={14} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {!readOnly && user?.role === "admin" && <LeadSourcesPanel />}
      {!readOnly && user?.role === "admin" && <AccountAccessPanel />}
      {!readOnly && user?.role === "admin" && <ClearDemoDataPanel />}
    </div>
  );
}

function AccountAccessPanel() {
  const [users, setUsers] = useState([]);
  const [resetting, setResetting] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/users/all");
      setUsers(data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const ROLE_BADGE = {
    admin: { color: "bg-zinc-900 text-white", label: "Admin" },
    viewer: { color: "bg-amber-100 text-amber-800", label: "Viewer" },
    readme_author: { color: "bg-rose-100 text-rose-700", label: "Read-Me Author" },
    client: { color: "bg-[#E5F0FF] text-[#0066ff]", label: "Client" },
  };

  return (
    <div data-testid="account-access-panel" className="mt-10 rounded-2xl border border-zinc-200 bg-white p-6">
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-amber-700">
            <Eye size={12} /> Account Access
          </div>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-zinc-900">All Logins</h2>
          <p className="mt-1 text-xs text-zinc-500">
            Recovery tool. Reset the password for any account — useful if a backup restore overwrites credentials.
          </p>
        </div>
      </div>

      <div className="mt-5 flex flex-col gap-2">
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : users.length === 0 ? (
          <div className="text-sm text-zinc-500">No users.</div>
        ) : users.map((u) => {
          const meta = ROLE_BADGE[u.role] || { color: "bg-zinc-100 text-zinc-700", label: u.role };
          return (
            <div key={u.user_id} data-testid={`account-row-${u.user_id}`} className="flex items-center justify-between rounded-md border border-zinc-100 bg-white px-3 py-2 hover:border-zinc-200 hover:bg-zinc-50/50">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-medium text-zinc-900">{u.name || u.email}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${meta.color}`}>{meta.label}</span>
                </div>
                <div className="truncate text-[11px] text-zinc-500">{u.email}</div>
              </div>
              <button
                data-testid={`account-reset-${u.user_id}`}
                onClick={() => setResetting(u)}
                className="ml-3 rounded-md border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-[#0066ff] hover:text-[#0066ff]"
              >
                Reset password
              </button>
            </div>
          );
        })}
      </div>

      {resetting && (
        <ResetPasswordModal user={resetting} onClose={() => setResetting(null)} onDone={() => { setResetting(null); }} />
      )}
    </div>
  );
}

function ResetPasswordModal({ user, onClose, onDone }) {
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pwd.length < 4) { toast.error("Use at least 4 characters"); return; }
    if (pwd !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api.put(`/users/${user.user_id}/password`, { password: pwd });
      toast.success(`Reset password for ${user.email}`);
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="account-reset-modal" className="w-full max-w-md rounded-2xl bg-white p-7 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-zinc-900">Reset Password</h2>
            <div className="mt-1 text-xs text-zinc-500">{user.name || user.email} · <span className="font-mono">{user.email}</span></div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-5 flex flex-col gap-3">
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">New password</span>
            <div className="mt-1 flex items-stretch overflow-hidden rounded-md border border-zinc-200 focus-within:border-[#0066ff] focus-within:ring-2 focus-within:ring-[#0066ff]/20">
              <input
                data-testid="account-reset-pwd"
                type={show ? "text" : "password"}
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                className="flex-1 px-3 py-2 text-sm focus:outline-none"
                autoFocus
              />
              <button type="button" onClick={() => setShow((s) => !s)} className="px-3 text-zinc-400 hover:text-zinc-700">
                {show ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <label className="text-sm">
            <span className="text-xs font-medium text-zinc-600">Confirm</span>
            <input
              data-testid="account-reset-confirm"
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="mt-1 w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20"
            />
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
          <button
            data-testid="account-reset-save"
            onClick={save}
            disabled={saving}
            className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0052cc] disabled:opacity-50"
          >
            {saving ? "Saving…" : "Reset password"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientModal({ mode = "create", initial = null, onClose, onSaved }) {
  const [form, setForm] = useState(initial ? { name: initial.name, email: initial.email, password: "" } : { name: "", email: "", password: "" });
  const [file, setFile] = useState(null);
  const [showPwd, setShowPwd] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email required");
      return;
    }
    if (mode === "create" && !form.password) {
      toast.error("Password required");
      return;
    }
    setSaving(true);
    try {
      let client;
      if (mode === "create") {
        const { data } = await api.post("/clients", form);
        client = data;
      } else {
        const payload = { name: form.name, email: form.email };
        if (form.password) payload.password = form.password;
        const { data } = await api.put(`/clients/${initial.user_id}`, payload);
        client = data;
      }
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`/clients/${client.user_id}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(mode === "create" ? "Client created" : "Client updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!initial) return;
    try {
      await api.delete(`/clients/${initial.user_id}`);
      toast.success("Client deleted");
      onSaved();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="client-modal" className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-zinc-500">{mode === "create" ? "New" : "Edit"}</div>
            <h2 className="mt-1 text-2xl font-semibold tracking-tight text-zinc-900">Client Account</h2>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 flex flex-col gap-4">
          <Field label="Name" required>
            <input data-testid="client-form-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} autoFocus />
          </Field>
          <Field label="Email" required>
            <input data-testid="client-form-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={inputCls} />
          </Field>
          <Field label={mode === "create" ? "Password" : "New password (leave blank to keep)"} required={mode === "create"}>
            <div className="relative">
              <input data-testid="client-form-password" type={showPwd ? "text" : "password"} value={form.password} onChange={(e) => set("password", e.target.value)} className={inputCls + " pr-9"} />
              <button type="button" onClick={() => setShowPwd((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-zinc-400 hover:bg-zinc-100">
                {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </Field>
          <Field label="Logo">
            <label className="flex cursor-pointer items-center gap-2 rounded-md border border-dashed border-zinc-300 bg-zinc-50 px-3 py-3 text-sm text-zinc-600 hover:border-[#0066ff] hover:text-[#0066ff]">
              <Upload size={14} />
              <input data-testid="client-form-logo" type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} className="hidden" />
              <span>{file ? file.name : (initial?.logo_file_id ? "Change logo" : "Upload logo (optional)")}</span>
            </label>
          </Field>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <div>
            {mode === "edit" && (
              confirmDelete ? (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-red-600">Sure?</span>
                  <button data-testid="client-delete-confirm" onClick={remove} className="rounded bg-red-500 px-2 py-1 font-medium text-white">Yes, delete</button>
                  <button onClick={() => setConfirmDelete(false)} className="rounded px-2 py-1 text-zinc-600">Cancel</button>
                </div>
              ) : (
                <button data-testid="client-delete" onClick={() => setConfirmDelete(true)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline"><Trash2 size={12} /> Delete client</button>
              )
            )}
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onClose} className="rounded-md px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-100">Cancel</button>
            <button data-testid="client-modal-save" onClick={save} disabled={saving} className="rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc] disabled:opacity-50">
              {saving ? "Saving…" : (mode === "create" ? "Create" : "Save")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClientProfileModal({ client, onClose, onGo }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div data-testid="client-profile-modal" className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-4">
            {client.logo_file_id ? (
              <img src={`${API_BASE}/files/${client.logo_file_id}`} alt="" className="h-16 w-16 rounded-xl object-cover" />
            ) : (
              <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-[#E5F0FF] text-xl font-semibold text-[#0066ff]">
                {(client.name || "?").charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold tracking-tight text-zinc-900">{client.name}</h2>
              <div className="mt-1 text-xs text-zinc-500">{client.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-zinc-400 hover:bg-zinc-100"><X size={18} /></button>
        </div>

        <div className="mt-6 grid grid-cols-3 gap-3">
          <Stat label="Received" value={client.tickets_total} />
          <Stat label="Active" value={client.tickets_active} accent="text-[#0066ff]" />
          <Stat label="Completed" value={client.tickets_complete} accent="text-emerald-600" />
        </div>

        <button data-testid="client-profile-view-orders" onClick={onGo} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0066ff] px-4 py-2 text-sm font-medium text-white hover:bg-[#0052cc]">
          <BarChart3 size={14} /> View all in Work Orders
        </button>
      </div>
    </div>
  );
}

function Stat({ label, value, accent = "text-zinc-900" }) {
  return (
    <div className="rounded-lg border border-zinc-100 bg-zinc-50 p-4 text-center">
      <div className={`text-2xl font-semibold ${accent}`}>{value ?? 0}</div>
      <div className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-zinc-500">{label}</div>
    </div>
  );
}

const inputCls = "w-full rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm placeholder:text-zinc-400 focus:border-[#0066ff] focus:outline-none focus:ring-2 focus:ring-[#0066ff]/20";

function Field({ label, required, children }) {
  return (
    <label className="flex flex-col gap-1.5 text-sm">
      <span className="text-xs font-medium text-zinc-600">{label}{required && <span className="text-red-500"> *</span>}</span>
      {children}
    </label>
  );
}
